import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Checkbox } from 'antd';
import { MessageIcon } from 'assets/icon/dynamic';
import { imgUrl } from 'utils/url';
import './TimelineSequence.css';

const TICK_STEP = 5;
const PX_PER_SEC = 16;
const ROW_H = 56;
const MIN_LOOP_DISPLAY = 90;

const rowsOf = (b) =>
  Array.isArray(b?.rows) && b.rows.length ? b.rows : [b?.row ?? 0];

const idOf = (m) => (typeof m === 'string' ? m : m?._id || m?.id);

const slotHue = (key) => {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

const slotColorStyle = (key) => {
  const hue = slotHue(key);
  return {
    background: `hsl(${hue}, 65%, 85%)`,
    borderColor: `hsl(${hue}, 60%, 45%)`,
    color: `hsl(${hue}, 65%, 25%)`,
  };
};

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const normalizeDay = (d) => {
  const v = String(d === 0 ? '0' : d || '').trim().toLowerCase();
  if (/^\d$/.test(v)) return DAY_KEYS[Number(v)] || null;
  const k = v.slice(0, 3);
  return DAY_KEYS.includes(k) ? k : null;
};

const toMinutes = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]);
  return Number.isNaN(mins) || mins < 0 || mins > 1440 ? null : mins;
};

const minuteIntervals = (startMin, endMin) => {
  if (startMin === null || endMin === null) return [[0, 1440]];
  if (endMin > startMin) return [[startMin, endMin]];
  return [
    [startMin, 1440],
    [0, endMin],
  ];
};

const scheduleWindowOf = (sch) => {
  const startDate = sch?.startDate ? new Date(sch.startDate).getTime() : null;
  const endDate = sch?.endDate ? new Date(sch.endDate).getTime() : null;
  let days = null;
  if (sch?.scheduleType === 'specificDays' && (sch.daysOfWeek || []).length > 0) {
    const parsed = (sch.daysOfWeek || []).map(normalizeDay).filter(Boolean);
    if (parsed.length > 0) days = new Set(parsed);
  }
  const timed = sch?.scheduleType === 'specificTime';
  return {
    start: Number.isFinite(startDate) ? startDate : -Infinity,
    end: Number.isFinite(endDate) ? endDate : Infinity,
    days,
    intervals: timed
      ? minuteIntervals(toMinutes(sch.startTime), toMinutes(sch.endTime))
      : [[0, 1440]],
  };
};

const rangesOverlap = (aS, aE, bS, bE) => aS < bE && bS < aE;

const windowsOverlap = (a, b) => {
  if (!(a.start <= b.end && b.start <= a.end)) return false;
  if (a.days && b.days && ![...a.days].some((d) => b.days.has(d))) return false;
  return a.intervals.some(([aS, aE]) =>
    b.intervals.some(([bS, bE]) => rangesOverlap(aS, aE, bS, bE))
  );
};

const mediaSrc = (m) => {
  if (!m || !m.logo) return null;
  return m.logo.startsWith('http') ? m.logo : `${imgUrl}/media/${m.logo}`;
};

const isVideoMedia = (m) => (m?.fileType || '').split('/')[0] === 'video';

const MediaStrip = ({ items, max = 3 }) => {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span
      className="ts-media-strip"
      title={list.map((m) => m?.name || idOf(m)).join(', ')}
    >
      {shown.map((m, i) => {
        const src = mediaSrc(m);
        const key = idOf(m) || i;
        if (!src) {
          return (
            <span key={key} className="ts-media-chip">
              <i className="feather-image" />
            </span>
          );
        }
        return isVideoMedia(m) ? (
          <span key={key} className="ts-media-chip">
            <i className="feather-video" />
          </span>
        ) : (
          <img key={key} src={src} alt={m?.name || ''} className="ts-media-thumb" />
        );
      })}
      {rest > 0 && <span className="ts-media-more">+{rest}</span>}
    </span>
  );
};

const Ruler = ({ displayLoop }) => {
  const ticks = [];
  for (let s = 0; s <= displayLoop; s += TICK_STEP) ticks.push(s);
  return (
    <div className="ts-ruler" style={{ width: `${displayLoop * PX_PER_SEC}px` }}>
      {ticks.map((t) => (
        <div
          key={t}
          className="ts-tick"
          style={{ left: `${t * PX_PER_SEC}px` }}
        >
          <span>{String(t).padStart(2, '0')}</span>
        </div>
      ))}
    </div>
  );
};

const TimelineSequence = ({
  screens,
  screenIds,
  timeline,
  blockDuration,
  blocks,
  setBlocks,
  editId,
  mediaCache,
  selectedMediaIds,
  conflict,
  schedule,
}) => {
  const ownWindow = useMemo(
    () => scheduleWindowOf(schedule),
    [
      schedule?.scheduleType,
      schedule?.startDate,
      schedule?.endDate,
      schedule?.startTime,
      schedule?.endTime,
      schedule?.daysOfWeek,
    ]
  );

  const overlappingBookings = (b) =>
    (b?.bookedBy || []).filter((x) => {
      if (editId && String(x.scheduleId) === String(editId)) return false;
      return windowsOverlap(ownWindow, scheduleWindowOf(x));
    });

  const screenRows = useMemo(() => {
    return (screenIds || []).map((sid) => {
      const tl = (timeline || []).find((t) => t.screenId === sid);
      const screen = (screens || []).find((s) => (s._id || s.id) === sid);
      const lanesData = Array.isArray(tl?.rows) ? tl.rows : [];

      const dedupedOther = new Map();
      lanesData.forEach((laneRow) => {
        (laneRow.blocks || []).forEach((b) => {
          if (b.source !== 'slot') return;
          const key = `${b.source}:${b.sourceId}:${rowsOf(b).join(',')}:${b.startOffset}`;
          const lane = Array.isArray(b?.rows) && b.rows.length
            ? b.rows[0]
            : laneRow.row ?? 0;
          if (!dedupedOther.has(key)) dedupedOther.set(key, { ...b, _lane: lane });
        });
      });
      const otherBlocks = Array.from(dedupedOther.values());

      const lanes = Array.from(new Set(otherBlocks.map((b) => b._lane))).sort(
        (a, b) => a - b
      );

      const slotIds = new Set(
        otherBlocks.map((b) => String(b.slotId || b.sourceId))
      );
      const slotPlacements = new Set(
        otherBlocks.map((b) => `${rowsOf(b).join(',')}::${b.startOffset}`)
      );

      return {
        screenId: sid,
        screenName: tl?.screenName || screen?.name || 'Screen',
        loopDuration: tl?.loopDuration > 0 ? tl.loopDuration : 0,
        lanes,
        otherBlocks,
        slotIds,
        slotPlacements,
        hasTemplate: otherBlocks.length > 0,
      };
    });
  }, [screenIds, timeline, screens, editId]);

  const onSlot = (row, b) => {
    if (!row) return false;
    if (b.slotId && row.slotIds.has(String(b.slotId))) return true;
    return row.slotPlacements.has(`${rowsOf(b).join(',')}::${b.startOffset}`);
  };

  useEffect(() => {
    if (!timeline || timeline.length === 0) return;
    const rowFor = new Map(screenRows.map((r) => [r.screenId, r]));
    setBlocks((prev) => {
      const next = prev.filter((b) => {
        const row = rowFor.get(b.screenId);
        if (!row) return true;
        return onSlot(row, b);
      });
      return next.length === prev.length ? prev : next;
    });
  }, [screenRows, timeline]);

  const [preview, setPreview] = useState({ open: false, seg: null });

  const removeOwn = (gidx) => {
    setBlocks((prev) => prev.filter((_, i) => i !== gidx));
  };

  const assignMediaToSlot = (screenId, slot) => {
    const rows = rowsOf(slot);
    const slotId = slot.slotId || slot.sourceId || slot.id;
    const existingIdx = (blocks || []).findIndex((b) => {
      if (b.screenId !== screenId) return false;
      if (b.slotId && slotId) return String(b.slotId) === String(slotId);
      return (
        b.startOffset === slot.startOffset &&
        rowsOf(b).join(',') === rows.join(',')
      );
    });
    if (existingIdx >= 0) {
      setPreview({
        open: true,
        seg: { ...blocks[existingIdx], source: 'own' },
        gidx: existingIdx,
      });
      return;
    }
    const newBlock = {
      screenId,
      rows,
      startOffset: slot.startOffset,
      duration: slot.duration || blockDuration || 0,
      mediaIds: selectedMediaIds || [],
      slotId,
    };
    const gidx = (blocks || []).length;
    setBlocks((prev) => [...prev, newBlock]);
    setPreview({ open: true, seg: { ...newBlock, source: 'own' }, gidx });
  };

  const toggleBlockMedia = (gidx, mediaId) => {
    setBlocks((prev) =>
      prev.map((b, i) => {
        if (i !== gidx) return b;
        const cur = (b.mediaIds || []).map(idOf);
        const next = cur.includes(mediaId)
          ? cur.filter((x) => x !== mediaId)
          : [...cur, mediaId];
        return { ...b, mediaIds: next };
      })
    );
  };

  const conflictHit = (screenId, b) => {
    if (!conflict) return false;
    const inRows = (cRows, bRows) =>
      Array.isArray(cRows) && Array.isArray(bRows)
        ? cRows.some((r) => bRows.includes(r))
        : true;
    if (Array.isArray(conflict.conflicts)) {
      const hit = conflict.conflicts.some(
        (c) =>
          c.screenId === screenId &&
          c.startOffset === b.startOffset &&
          inRows(c.rows, rowsOf(b))
      );
      if (hit) return true;
      const againstHit = conflict.conflicts.some(
        (c) =>
          c.against &&
          b.sourceId &&
          c.against.sourceId === b.sourceId
      );
      if (againstHit) return true;
    }
    if (Array.isArray(conflict.capacityViolations)) {
      if (
        conflict.capacityViolations.some(
          (c) =>
            c.screenId === screenId &&
            c.startOffset === b.startOffset &&
            inRows(c.rows, rowsOf(b))
        )
      )
        return true;
    }
    if (
      conflict.selfOverlap &&
      conflict.selfOverlap.screenId === screenId
    ) {
      const { a, b: cb, rows: cRows } = conflict.selfOverlap;
      if (
        (a?.startOffset === b.startOffset || cb?.startOffset === b.startOffset) &&
        inRows(cRows, rowsOf(b))
      )
        return true;
    }
    return false;
  };

  const renderSlotBlock = (screenId, b, mine) => {
    const lane = b._lane != null ? b._lane : Math.min(...rowsOf(b));
    const top = lane * ROW_H + 24 + 4;
    const left = b.startOffset * PX_PER_SEC;
    const width = (b.duration || 0) * PX_PER_SEC;
    const others = overlappingBookings(b);
    const isMine = !!mine;
    const myMedia = isMine
      ? (mine.b.mediaIds || [])
          .map((mid) => {
            const mediaId = idOf(mid);
            return (
              (mediaCache && mediaCache[mediaId]) ||
              (typeof mid === 'object' ? mid : { _id: mediaId })
            );
          })
          .filter(Boolean)
      : [];

    let cls = 'ts-seg';
    if (isMine) {
      cls += ' ts-available selected';
      if (myMedia.length === 0) cls += ' ts-unassigned';
    } else {
      cls += ' ts-fully-booked ts-slot-other';
      if (others.length) cls += ' ts-slot-booked';
    }
    if (conflictHit(screenId, b) || (isMine && conflictHit(screenId, mine.b)))
      cls += ' conflict';

    const slotStyle = !isMine && b.templateId ? slotColorStyle(b.templateId) : null;

    const otherMedia = [
      ...(b.mediaIds || []),
      ...others.flatMap((x) => x.mediaIds || []),
    ];

    let title;
    if (isMine) {
      const alsoBooked = others.length
        ? ` Also booked by ${others.map((x) => x.name).join(', ')}.`
        : '';
      title = myMedia.length
        ? `${b.name} — your booking plays ${myMedia
            .map((m) => m?.name || idOf(m))
            .join(', ')}.${alsoBooked}`
        : `${b.name} — your booking, no media assigned yet. Click to assign.${alsoBooked}`;
    } else if (others.length) {
      title = `${b.name} — booked by ${others
        .map((x) => x.name)
        .join(', ')}. Click to book it too.`;
    } else {
      title = `Assign media to slot: ${b.name}`;
    }

    return (
      <div
        key={`slot:${b.sourceId}`}
        className={cls}
        style={{
          left: `${left}px`,
          width: `${width}px`,
          top: `${top}px`,
          height: `${ROW_H - 8}px`,
          position: 'absolute',
          cursor: 'pointer',
          ...(slotStyle || {}),
        }}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          if (isMine) {
            setPreview({
              open: true,
              seg: { ...mine.b, name: b.name, source: 'own' },
              gidx: mine.gidx,
            });
          } else {
            assignMediaToSlot(screenId, b);
          }
        }}
      >
        <span className="ts-dur">{b.duration}s</span>
        <span className="ts-status">
          <span className="ts-status-text">
            {isMine && myMedia.length === 0
              ? `${b.name || 'Slot'} · no media`
              : b.name || 'Slot'}
          </span>
          <MediaStrip items={isMine ? myMedia : otherMedia} />
          {!isMine && others.length > 0 && (
            <span className="ts-booked-by">{others.length} booked</span>
          )}
          {isMine ? (
            <button
              type="button"
              className="ts-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeOwn(mine.gidx);
              }}
              draggable={false}
              title="Remove booking"
            >
              <i className="feather-x" />
            </button>
          ) : (
            <i className="feather-bookmark ts-bm" />
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="ts-wrap">
      <h3 className="section-title">Timeline Sequence</h3>

      <div className="ts-rows">
        {screenRows.map((row) => {
          const loop = row.loopDuration || 0;
          const ownInScreen = (blocks || [])
            .map((b, i) => ({ b, gidx: i }))
            .filter(({ b }) => b.screenId === row.screenId && onSlot(row, b));
          const mineBySlot = new Map();
          ownInScreen.forEach(({ b, gidx }) => {
            if (b.slotId) mineBySlot.set(`id:${String(b.slotId)}`, { b, gidx });
            mineBySlot.set(
              `at:${rowsOf(b).join(',')}::${b.startOffset}`,
              { b, gidx }
            );
          });
          const mineFor = (slot) =>
            mineBySlot.get(`id:${String(slot.slotId || slot.sourceId)}`) ||
            mineBySlot.get(
              `at:${rowsOf(slot).join(',')}::${slot.startOffset}`
            ) ||
            null;
          const contentEnd = row.otherBlocks.length
            ? Math.max(
                ...row.otherBlocks.map((b) => b.startOffset + (b.duration || 0))
              )
            : 0;
          const displayLoop = Math.max(loop, contentEnd) || MIN_LOOP_DISPLAY;
          const trackWidthPx = displayLoop * PX_PER_SEC;
          const { lanes } = row;
          const trackHeight = lanes.length * ROW_H + 24;

          return (
            <div className="ts-row" key={row.screenId}>
              <div className="ts-row-side">
                <MessageIcon size={28} color="#64D5D2" />
                <div className="ts-row-name">
                  {row.screenName.toUpperCase()}
                </div>
              </div>

              {!row.hasTemplate ? (
                <div className="ts-row-main">
                  <div className="ts-no-template">
                    No template configured for this screen. Assign a template to
                    the screen first to see bookable slots.
                  </div>
                </div>
              ) : (
              <div className="ts-row-main">
                <div className="ts-template-name">
                  Loop {loop || '—'}s · {lanes.length} lane{lanes.length > 1 ? 's' : ''}
                </div>
                <div className="ts-track-shell">
                  <div className="ts-scroll">
                    <div
                      className="ts-scroll-inner"
                      style={{ width: `${trackWidthPx}px` }}
                    >
                      <Ruler displayLoop={displayLoop} />
                      <div
                        className="ts-multi-track"
                        style={{
                          position: 'relative',
                          width: `${trackWidthPx}px`,
                          height: `${trackHeight}px`,
                        }}
                      >
                        {lanes.map((lane, i) => (
                          <div
                            key={`lane-${lane}`}
                            className="ts-lane"
                            style={{
                              position: 'absolute',
                              top: `${i * ROW_H + 24}px`,
                              left: 0,
                              width: `${trackWidthPx}px`,
                              height: `${ROW_H}px`,
                              background: '#F8FAFC',
                            }}
                          >
                            <span className="ts-lane-label">lane {lane}</span>
                          </div>
                        ))}

                        {row.otherBlocks.map((b) =>
                          renderSlotBlock(row.screenId, b, mineFor(b))
                        )}

                        {loop > 0 && (
                          <div
                            className="ts-loop-end"
                            style={{
                              position: 'absolute',
                              left: `${loop * PX_PER_SEC}px`,
                              top: 24,
                              bottom: 0,
                              width: 2,
                              background: '#EF4444',
                              opacity: 0.5,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal
        open={preview.open}
        onCancel={() => setPreview({ open: false, seg: null })}
        footer={null}
        title={
          preview.seg
            ? preview.seg.source === 'own'
              ? 'Assign Media — Block'
              : `Preview — ${preview.seg.name || 'Booking'}`
            : 'Preview'
        }
        width={560}
      >
        {preview.seg && (() => {
          const isOwn = preview.seg.source === 'own';

          const renderMediaRow = (mid, extra) => {
            const id = idOf(mid);
            const m =
              (mediaCache && mediaCache[id]) ||
              (typeof mid === 'object' ? mid : null);
            if (!m) return null;
            const isVideo = m.fileType && m.fileType.split('/')[0] === 'video';
            const src = m.logo
              ? m.logo.startsWith('http')
                ? m.logo
                : `${imgUrl}/media/${m.logo}`
              : null;
            return (
              <div key={id} className="ts-preview-row">
                {extra}
                {src && !isVideo && (
                  <img src={src} alt={m.name} className="ts-preview-img" />
                )}
                {src && isVideo && (
                  <video src={src} controls className="ts-preview-img" />
                )}
                <div className="ts-preview-meta">
                  <div className="ts-preview-name">{m.name || 'Media'}</div>
                  {m.duration != null && (
                    <div className="ts-preview-dur">{m.duration}s</div>
                  )}
                </div>
              </div>
            );
          };

          if (isOwn) {
            const pool = selectedMediaIds || [];
            if (!pool.length) {
              return (
                <div className="ts-preview-empty">
                  No media in schedule to assign. Add media above first.
                </div>
              );
            }
            const liveBlock =
              preview.gidx != null ? (blocks || [])[preview.gidx] : null;
            const assigned = (liveBlock?.mediaIds || []).map(idOf);
            return (
              <div className="ts-preview-list">
                {pool.map((mid) => {
                  const id = idOf(mid);
                  const checkbox = (
                    <Checkbox
                      checked={assigned.includes(id)}
                      onChange={() => toggleBlockMedia(preview.gidx, id)}
                    />
                  );
                  return renderMediaRow(mid, checkbox);
                })}
              </div>
            );
          }

          const bookings = overlappingBookings(preview.seg);
          const ids = preview.seg.mediaIds || [];
          if (!ids.length && bookings.length === 0) {
            return <div className="ts-preview-empty">No media assigned to this slot.</div>;
          }
          return (
            <div className="ts-preview-list">
              {ids.length > 0 && (
                <div className="ts-preview-group">Assigned by template</div>
              )}
              {ids.map((mid) => renderMediaRow(mid))}
              {bookings.map((bk) => (
                <div key={String(bk.scheduleId)}>
                  <div className="ts-preview-group">
                    Booked by “{bk.name}”
                    {bk.startTime && bk.endTime
                      ? ` (${bk.startTime}–${bk.endTime} UTC)`
                      : ''}
                  </div>
                  {(bk.mediaIds || []).length === 0 ? (
                    <div className="ts-preview-empty">No media assigned.</div>
                  ) : (
                    (bk.mediaIds || []).map((mid) => renderMediaRow(mid))
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default TimelineSequence;
